import React from 'react';
import { Box, Typography, Chip } from '@mui/material';

interface BenchmarkLegendItem {
    value: string;
    type: string;
    color: string;
    payload: {
        isBenchmark?: boolean;
        dataKey: string;
        stroke?: string;
        fill?: string;
    };
}

interface BenchmarkLegendProps {
    payload?: BenchmarkLegendItem[];
    getMetricInfo: (metricKey: string) => { label: string; color: string; unit: string };
}

const BenchmarkLegend: React.FC<BenchmarkLegendProps> = ({ payload = [], getMetricInfo }) => {
    if (!payload || payload.length === 0) {
        return null;
    }

    // Group legend items by metric type (regular vs benchmark)
    const regularItems = payload.filter(item => !item.payload?.isBenchmark);
    const benchmarkItems = payload.filter(item => item.payload?.isBenchmark);

    const renderLegendItem = (item: BenchmarkLegendItem, isBenchmark: boolean = false) => {
        const dataKey = item.payload?.dataKey || item.value;
        const originalKey = isBenchmark ? dataKey.replace('_benchmark', '') : dataKey;
        const metricInfo = getMetricInfo(originalKey);
        const color = item.color || item.payload?.stroke || item.payload?.fill || metricInfo.color;

        return (
            <Box
                key={item.value}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 0.5,
                    borderRadius: 0.5,
                    bgcolor: isBenchmark ? 'grey.50' : 'transparent',
                    border: isBenchmark ? '1px dashed' : 'none',
                    borderColor: isBenchmark ? 'grey.300' : 'transparent'
                }}
            >
                {/* Color indicator */}
                <Box
                    sx={{
                        width: 16,
                        height: isBenchmark ? 2 : 3,
                        bgcolor: color,
                        borderRadius: isBenchmark ? 0 : 1,
                        border: isBenchmark ? 'none' : `2px solid ${color}`,
                        backgroundImage: isBenchmark ? `repeating-linear-gradient(90deg, ${color} 0, ${color} 2px, transparent 2px, transparent 4px)` : 'none'
                    }}
                />
                
                {/* Label */}
                <Typography
                    variant="caption"
                    sx={{
                        color: isBenchmark ? 'text.secondary' : 'text.primary',
                        fontStyle: isBenchmark ? 'italic' : 'normal',
                        fontSize: isBenchmark ? '0.7rem' : '0.75rem'
                    }}
                >
                    {metricInfo.label}
                    {metricInfo.unit && ` (${metricInfo.unit})`}
                </Typography>

                {/* Benchmark indicator */}
                {isBenchmark && (
                    <Chip
                        label="Avg"
                        size="small"
                        variant="outlined"
                        sx={{
                            fontSize: '0.6rem',
                            height: 16,
                            '& .MuiChip-label': { px: 0.5 },
                            color: 'text.secondary',
                            borderColor: 'grey.400'
                        }}
                    />
                )}
            </Box>
        );
    };

    return (
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            {/* Regular metrics */}
            {regularItems.length > 0 && (
                <Box sx={{ mb: benchmarkItems.length > 0 ? 1 : 0 }}>
                    <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                        Your Data:
                    </Typography>
                    <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        maxWidth: '100%'
                    }}>
                        {regularItems.map(item => renderLegendItem(item, false))}
                    </Box>
                </Box>
            )}

            {/* Benchmark metrics */}
            {benchmarkItems.length > 0 && (
                <Box>
                    <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5, color: 'text.secondary' }}>
                        Benchmark Comparison:
                    </Typography>
                    <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        maxWidth: '100%'
                    }}>
                        {benchmarkItems.map(item => renderLegendItem(item, true))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default BenchmarkLegend;
